import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export type NotificationCategory =
  | 'incoming_calls'
  | 'missed_calls'
  | 'voicemail'
  | 'messaging'
  | 'sms_inbound'
  | 'whatsapp_inbound'
  | 'telegram_inbound'
  | 'chat_message'
  | 'chat_mention'
  | 'email_received'
  | 'feed_post'
  | 'feed_comment'
  | 'feed_mention'
  | 'feed_reaction'
  | 'feed_report'
  | 'system_alert'
  | 'service_alert'
  | 'storage_alert'
  | 'storage_quota'
  | 'calendar_appointment'
  | 'calendar_task'
  | 'calendar_videocall'
  | 'workflow_notification'
  | 'invoice_lifecycle'
  | 'hr_request'
  | 'task_event'
  | 'crm_event'
  | 'lead_inbound'
  | 'video_invitation'
  | 'support_ticket'
  | 'workgroup_invite'
  | 'appointment_reassigned'
  | 'sip_admin'
  | 'pos_fiscal'
  | 'pos_logistics'
  | 'warning';

export interface CategoryPreference {
  category: NotificationCategory;
  in_app_enabled: boolean;
  push_enabled: boolean;
  email_enabled: boolean;
  sound_enabled: boolean;
  /** Task #1418 — invio in chat Telegram (richiede pairing utente). */
  telegram_enabled: boolean;
  first_reminder_minutes?: number | null;
  second_reminder_minutes?: number | null;
  /** Per-category sound id (e.g. "default", "classic"). Null = default tone. */
  tone?: string | null;
}

export interface QuietHoursPreference {
  enabled: boolean;
  start_time: string | null;
  end_time: string | null;
  /** When true, incoming calls are still audible during the quiet window. */
  allow_critical_calls: boolean;
}

export interface EmailAccountOverride {
  email_account_id: string;
  enabled: boolean;
}

export interface NotificationPreferencesPayload {
  categories: CategoryPreference[];
  quiet_hours: QuietHoursPreference;
  email_account_overrides: EmailAccountOverride[];
}

export type CategoryUpdate = Partial<Omit<CategoryPreference, 'category'>> & { category: NotificationCategory };

export interface SavePreferencesInput {
  categories?: CategoryUpdate[];
  quiet_hours?: QuietHoursPreference;
  email_account_overrides?: EmailAccountOverride[];
}

export interface UseNotificationPreferencesResult {
  data: NotificationPreferencesPayload | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  save: (payload: SavePreferencesInput) => Promise<boolean>;
  saving: boolean;
}

/**
 * Stable cache key. All consumers (Settings tab, NotificationHandler, gate
 * helpers) share this single React Query entry — the first to mount triggers
 * the fetch, every subsequent consumer reads from cache instantly.
 */
export const NOTIFICATION_PREFS_QUERY_KEY = ['notifications', 'preferences'] as const;

const EMPTY_PAYLOAD: NotificationPreferencesPayload = {
  categories: [],
  quiet_hours: { enabled: false, start_time: null, end_time: null, allow_critical_calls: true },
  email_account_overrides: [],
};

function normalizeEnvelope(res: unknown): NotificationPreferencesPayload {
  // apiClient returns { success, data }; the route wraps the payload again
  // inside `data` — successResponse(res, payload). Tolerate both shapes.
  const asRec = (v: unknown): Record<string, unknown> | undefined =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
  const top = asRec(res);
  const lvl1 = asRec(top?.data);
  const inner = asRec(lvl1?.data) ?? lvl1 ?? top;
  if (!inner || !Array.isArray(inner.categories)) return EMPTY_PAYLOAD;
  const qhSrc = asRec(inner.quiet_hours) ?? {};
  return {
    categories: inner.categories as CategoryPreference[],
    quiet_hours: {
      enabled: !!qhSrc.enabled,
      start_time: (qhSrc.start_time as string | null | undefined) ?? null,
      end_time: (qhSrc.end_time as string | null | undefined) ?? null,
      allow_critical_calls: qhSrc.allow_critical_calls !== false,
    },
    email_account_overrides: Array.isArray(inner.email_account_overrides)
      ? (inner.email_account_overrides as EmailAccountOverride[])
      : [],
  };
}

async function fetchPreferences(): Promise<NotificationPreferencesPayload> {
  const res = await apiClient.get('/notifications/preferences');
  return normalizeEnvelope(res);
}

/**
 * Centralized React hook for the unified user notification preferences. Wraps
 * GET /api/notifications/preferences (read) and PUT /api/notifications/preferences
 * (partial merge per category) on top of the project-wide React Query cache,
 * so the Settings tab, NotificationHandler and any other consumer share a
 * single source of truth without duplicate network requests.
 */
export function useNotificationPreferences(enabled = true): UseNotificationPreferencesResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: NOTIFICATION_PREFS_QUERY_KEY,
    queryFn: fetchPreferences,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: EMPTY_PAYLOAD,
  });

  const mutation = useMutation({
    mutationFn: async (payload: SavePreferencesInput) => {
      const body: Record<string, unknown> = {};
      if (payload.categories && payload.categories.length > 0) body.categories = payload.categories;
      if (payload.quiet_hours) body.quiet_hours = payload.quiet_hours;
      if (payload.email_account_overrides) body.email_account_overrides = payload.email_account_overrides;
      await apiClient.put('/notifications/preferences', body);
      return payload;
    },
    onSuccess: (payload) => {
      queryClient.setQueryData<NotificationPreferencesPayload>(NOTIFICATION_PREFS_QUERY_KEY, prev => {
        const base: NotificationPreferencesPayload = prev ?? EMPTY_PAYLOAD;
        const next: NotificationPreferencesPayload = {
          categories: base.categories.map(c => ({ ...c })),
          quiet_hours: { ...base.quiet_hours },
          email_account_overrides: base.email_account_overrides.map(o => ({ ...o })),
        };
        if (payload.categories) {
          for (const upd of payload.categories) {
            const idx = next.categories.findIndex(c => c.category === upd.category);
            if (idx >= 0) {
              const base = next.categories[idx];
              if (!base) continue;
              const merged: CategoryPreference = {
                ...base,
                ...(upd.in_app_enabled !== undefined && { in_app_enabled: upd.in_app_enabled }),
                ...(upd.push_enabled !== undefined && { push_enabled: upd.push_enabled }),
                ...(upd.email_enabled !== undefined && { email_enabled: upd.email_enabled }),
                ...(upd.sound_enabled !== undefined && { sound_enabled: upd.sound_enabled }),
                ...(upd.telegram_enabled !== undefined && { telegram_enabled: upd.telegram_enabled }),
                ...(upd.first_reminder_minutes !== undefined && { first_reminder_minutes: upd.first_reminder_minutes }),
                ...(upd.second_reminder_minutes !== undefined && { second_reminder_minutes: upd.second_reminder_minutes }),
                ...(upd.tone !== undefined && { tone: upd.tone }),
              };
              next.categories[idx] = merged;
            } else {
              next.categories.push({
                category: upd.category,
                in_app_enabled: upd.in_app_enabled ?? true,
                push_enabled: upd.push_enabled ?? true,
                email_enabled: upd.email_enabled ?? false,
                sound_enabled: upd.sound_enabled ?? true,
                telegram_enabled: upd.telegram_enabled ?? false,
                first_reminder_minutes: upd.first_reminder_minutes ?? null,
                second_reminder_minutes: upd.second_reminder_minutes ?? null,
                tone: upd.tone ?? null,
              });
            }
          }
        }
        if (payload.quiet_hours) next.quiet_hours = { ...payload.quiet_hours };
        if (payload.email_account_overrides) next.email_account_overrides = payload.email_account_overrides.map(o => ({ ...o }));
        return next;
      });
    },
  });

  const save = useCallback(async (payload: SavePreferencesInput): Promise<boolean> => {
    try {
      await mutation.mutateAsync(payload);
      return true;
    } catch {
      return false;
    }
  }, [mutation]);

  const reload = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFS_QUERY_KEY });
  }, [queryClient]);

  const errorMessage = useMemo(() => {
    const e = mutation.error || query.error;
    if (!e) return null;
    const apiErr = e as { response?: { data?: { error?: string } }; message?: string };
    return apiErr?.response?.data?.error || apiErr?.message || 'Errore preferenze notifiche';
  }, [query.error, mutation.error]);

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: errorMessage,
    reload,
    save,
    saving: mutation.isPending,
  };
}

/**
 * Read-only convenience: returns the matrix indexed by category for fast
 * lookups in non-settings code that needs to check whether a vehicle is
 * enabled for a given notification.
 */
export function indexByCategory(prefs: NotificationPreferencesPayload | null): Record<string, CategoryPreference> {
  const out: Record<string, CategoryPreference> = {};
  if (!prefs) return out;
  for (const c of prefs.categories) out[c.category] = c;
  return out;
}

export type NotificationVehicle = 'in_app' | 'push' | 'email' | 'sound' | 'telegram';

function isWithinQuietHours(qh: QuietHoursPreference, now: Date = new Date()): boolean {
  if (!qh.enabled || !qh.start_time || !qh.end_time) return false;
  const toMinutes = (t: string): number | null => {
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t);
    if (!m) return null;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
    return h * 60 + mm;
  };
  const start = toMinutes(qh.start_time);
  const end = toMinutes(qh.end_time);
  if (start === null || end === null) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  // Wrap-around windows like 22:00 → 07:00.
  if (start <= end) return cur >= start && cur < end;
  return cur >= start || cur < end;
}

const CRITICAL_CATEGORIES: ReadonlySet<NotificationCategory> = new Set(['incoming_calls']);

export interface NotificationGate {
  /**
   * Returns true if the given vehicle is allowed for the given category right
   * now, taking into account category preferences AND quiet hours (with the
   * `allow_critical_calls` exemption for incoming calls).
   */
  isAllowed: (category: NotificationCategory, vehicle: NotificationVehicle) => boolean;
  /**
   * Returns true if the user has enabled notifications for this email account
   * (per-account override). Defaults to `true` when no override row exists.
   */
  isEmailAccountAllowed: (emailAccountId: string | null | undefined) => boolean;
  /**
   * Returns the per-category tone id ("default" if unset).
   */
  toneFor: (category: NotificationCategory) => string;
  /** True while the underlying preferences are still loading from the server. */
  loading: boolean;
}

/**
 * Lightweight hook for runtime notification gating. Designed to be consumed by
 * components that need to decide *whether* to play a sound / show a desktop
 * popup / route an event — not by the Settings UI. Internally shares the same
 * React Query cache as `useNotificationPreferences`, so wiring it into many
 * components is essentially free.
 */
export function useNotificationGate(enabled = true): NotificationGate {
  const { data, loading } = useNotificationPreferences(enabled);

  return useMemo<NotificationGate>(() => {
    const byCategory = indexByCategory(data);
    const overrides = new Map<string, boolean>();
    for (const o of data?.email_account_overrides || []) overrides.set(o.email_account_id, o.enabled);
    const qh = data?.quiet_hours || EMPTY_PAYLOAD.quiet_hours;
    const inQuiet = isWithinQuietHours(qh);

    const isAllowed = (category: NotificationCategory, vehicle: NotificationVehicle): boolean => {
      const pref = byCategory[category];
      const vehicleKey = `${vehicle}_enabled` as
        | 'in_app_enabled' | 'push_enabled' | 'email_enabled' | 'sound_enabled' | 'telegram_enabled';
      // When no pref row exists yet (before first backend response), default
      // to allow (fail-open) — the backend has already gated the push.
      // Telegram is opt-in so it defaults to false even when the row is absent.
      const baseEnabled = pref
        ? pref[vehicleKey]
        : vehicle === 'telegram' ? false : vehicle === 'email' ? false : true;
      if (!baseEnabled) return false;
      if (inQuiet) {
        if (vehicle === 'email') {
          // Email digests/mirrors are tolerable during quiet hours; we only
          // mute push and sound channels (and skip in-app toasts).
          return true;
        }
        const isCritical = CRITICAL_CATEGORIES.has(category);
        if (isCritical && qh.allow_critical_calls) return true;
        return false;
      }
      return true;
    };

    const isEmailAccountAllowed = (emailAccountId?: string | null): boolean => {
      if (!emailAccountId) return true;
      const v = overrides.get(emailAccountId);
      return v === undefined ? true : v;
    };

    const toneFor = (category: NotificationCategory): string => {
      return byCategory[category]?.tone || 'default';
    };

    return { isAllowed, isEmailAccountAllowed, toneFor, loading };
  }, [data, loading]);
}
